import React, { useEffect, useRef, useState } from 'react';
import SidebarCalendar from './SidebarCalendar';

const LOGO_SRC = process.env.PUBLIC_URL + '/logo.png';

const NAV = [
  { sec: 'Main',      items: [
    { id: 'dash',    icon: 'dashboard',        label: 'Dashboard'       },
    { id: 'notices', icon: 'campaign',         label: 'Notice Board'    },
    { id: 'msg',     icon: 'mail',             label: 'Messages'        },
  ]},
  { sec: 'People',    items: [
    { id: 'stu',     icon: 'school',           label: 'Students'        },
    { id: 'tea',     icon: 'person',           label: 'Teachers'        },
    { id: 'adm',     icon: 'how_to_reg',       label: 'Admissions'      },
  ]},
  { sec: 'Academics', items: [
    { id: 'cls',     icon: 'class',            label: 'Classes'         },
    { id: 'tt',      icon: 'grid_view',        label: 'Timetable'       },
    { id: 'att',     icon: 'event_available',  label: 'Attendance'      },
    { id: 'satt',    icon: 'how_to_reg',       label: 'Staff Attendance' },
    { id: 'exam',    icon: 'quiz',             label: 'Exams'           },
    { id: 'cal',     icon: 'calendar_month',   label: 'Calendar'        },
  ]},
  { sec: 'Finance',   items: [
    { id: 'fee',     icon: 'payments',         label: 'Fees'            },
  ]},
  { sec: 'Tools',     items: [
    { id: 'docs',    icon: 'badge',            label: 'Documents'       },
    { id: 'rep',     icon: 'bar_chart',        label: 'Reports'         },
    { id: 'set',     icon: 'settings',         label: 'Settings'        },
  ]},
];
const Sidebar = React.memo(function Sidebar({ page, setPage, user, onLogout, isMobile = false, mobileOpen = false, onMobileClose = () => { } }) {
  const ref = React.useRef(null);
  const open = React.useRef(false);

  const expand = () => {
    if (isMobile) return;
    if (open.current) return;
    open.current = true;
    const el = ref.current; if (!el) return;
    el.style.width = '230px';
    el.querySelectorAll('.sb-label').forEach(n => { n.style.opacity = '1'; n.style.maxWidth = '180px'; });
    el.querySelectorAll('.sb-sec').forEach(n => { n.style.opacity = '1'; n.style.maxHeight = '24px'; });
    el.querySelectorAll('.sb-btn').forEach(n => { n.style.justifyContent = 'flex-start'; n.style.padding = '10px 12px'; });
    el.querySelectorAll('.sb-user').forEach(n => { n.style.opacity = '1'; n.style.maxWidth = '160px'; });
    el.querySelectorAll('.sb-active-pill').forEach(n => { n.style.opacity = '1'; });
  };

  const collapse = () => {
    if (isMobile) return;
    if (!open.current) return;
    open.current = false;
    const el = ref.current; if (!el) return;
    el.style.width = '60px';
    el.querySelectorAll('.sb-label').forEach(n => { n.style.opacity = '0'; n.style.maxWidth = '0'; });
    el.querySelectorAll('.sb-sec').forEach(n => { n.style.opacity = '0'; n.style.maxHeight = '0'; });
    el.querySelectorAll('.sb-btn').forEach(n => { n.style.justifyContent = 'center'; n.style.padding = '10px 0'; });
    el.querySelectorAll('.sb-user').forEach(n => { n.style.opacity = '0'; n.style.maxWidth = '0'; });
    el.querySelectorAll('.sb-active-pill').forEach(n => { n.style.opacity = '0'; });
  };

  // section accent colors
  const secColor = { Main: '#63b3ff', People: '#86efac', Academics: '#fbbf24', Finance: '#f9a8d4', Tools: '#c4b5fd' };

  React.useEffect(() => {
    const el = ref.current; if (!el) return;
    if (isMobile) {
      // On mobile, drawer is fully expanded when opened.
      el.querySelectorAll('.sb-label').forEach(n => { n.style.opacity = '1'; n.style.maxWidth = '180px'; });
      el.querySelectorAll('.sb-sec').forEach(n => { n.style.opacity = '1'; n.style.maxHeight = '24px'; });
      el.querySelectorAll('.sb-btn').forEach(n => { n.style.justifyContent = 'flex-start'; n.style.padding = '10px 12px'; });
      el.querySelectorAll('.sb-user').forEach(n => { n.style.opacity = '1'; n.style.maxWidth = '160px'; });
      el.querySelectorAll('.sb-active-pill').forEach(n => { n.style.opacity = '1'; });
      open.current = true;
      return;
    }
    // Reset desktop default collapsed state.
    collapse();
  }, [isMobile]);

  return (
    <aside ref={ref} onMouseEnter={isMobile ? undefined : expand} onMouseLeave={isMobile ? undefined : collapse}
      style={{
        width: isMobile ? 230 : 60, flexShrink: 0, zIndex: isMobile ? 1200 : 50, overflow: 'hidden',
        display: 'flex', flexDirection: 'column',
        position: isMobile ? 'fixed' : 'relative',
        left: 0, top: isMobile ? 0 : undefined, bottom: isMobile ? 0 : undefined,
        transform: isMobile ? (mobileOpen ? 'translateX(0)' : 'translateX(-110%)') : 'none',
        transition: isMobile ? 'transform 220ms ease' : 'width 260ms cubic-bezier(0.4,0,0.2,1)',
        backgroundColor: '#1a3a5c',
        backgroundImage: `
          radial-gradient(ellipse at 0% 0%, rgba(99,179,255,0.12) 0%, transparent 60%),
          radial-gradient(ellipse at 100% 100%, rgba(99,102,241,0.1) 0%, transparent 60%),
          url("data:image/svg+xml,%3Csvg width='24' height='24' viewBox='0 0 24 24' xmlns='http://www.w3.org/2000/svg'%3E%3Ccircle cx='1' cy='1' r='0.8' fill='%23ffffff' fill-opacity='0.04'/%3E%3C/svg%3E")
        `,
        backgroundSize: 'auto,auto,24px 24px',
        boxShadow: '4px 0 20px rgba(0,20,60,0.18)',
      }}>

      {/* Logo row */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, height: 64, flexShrink: 0,
        padding: '0 13px',
        borderBottom: '1px solid rgba(255,255,255,0.07)'
      }}>
        <img src={LOGO_SRC} alt="LKPS" style={{
          width: 34, height: 34, minWidth: 34, maxWidth: 34, objectFit: 'contain', flexShrink: 0, flexGrow: 0,
          filter: 'drop-shadow(0 2px 6px rgba(0,0,0,0.4))'
        }}
          onError={e => e.target.style.display = 'none'} />
        <div className="sb-label" style={{ opacity: 0, maxWidth: 0, overflow: 'hidden', transition: 'opacity 220ms,max-width 260ms', whiteSpace: 'nowrap', flexShrink: 0 }}>
          <div style={{ color: '#fff', fontWeight: 900, fontSize: 15, fontFamily: 'Manrope,sans-serif', letterSpacing: '0.06em' }}>LKPS</div>
          <div style={{ color: 'rgba(255,255,255,0.35)', fontSize: 9, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', marginTop: 1 }}>Admin Portal</div>
        </div>
      </div>

      {/* Nav */}
      <nav style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', padding: '8px 6px 0', scrollbarWidth: 'none' }}>
        <style>{`aside::-webkit-scrollbar{display:none}`}</style>
        {NAV.map((g, gi) => (
          <div key={g.sec} style={{ marginBottom: 4 }}>
            {/* section label */}
            <div className="sb-sec" style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '8px 10px 4px',
              opacity: 0, maxHeight: 0, overflow: 'hidden',
              transition: 'opacity 200ms,max-height 240ms',
            }}>
              <div style={{ height: 1, width: 10, background: secColor[g.sec] || 'rgba(255,255,255,0.2)', borderRadius: 1, flexShrink: 0 }} />
              <span style={{ color: secColor[g.sec] || 'rgba(255,255,255,0.3)', fontSize: 9, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.12em', whiteSpace: 'nowrap' }}>{g.sec}</span>
            </div>

            {g.items.map(it => {
              const active = page === it.id;
              return (
                <button key={it.id} onClick={() => { setPage(it.id); if (isMobile) onMobileClose(); }}
                  title={it.label}
                  className="sb-btn"
                  style={{
                    position: 'relative', display: 'flex', alignItems: 'center', width: '100%',
                    border: 'none', borderRadius: 10, cursor: 'pointer', marginBottom: 2,
                    padding: '10px 0', justifyContent: 'center', gap: 0,
                    background: active ? 'rgba(255,255,255,0.12)' : 'transparent',
                    color: active ? '#fff' : 'rgba(255,255,255,0.5)',
                    fontSize: 13, fontWeight: 600, fontFamily: 'Manrope,sans-serif',
                    transition: 'background 180ms,color 180ms,transform 120ms',
                    boxShadow: active ? 'inset 0 0 0 1px rgba(255,255,255,0.1)' : 'none',
                    minHeight: 40,
                  }}
                  onMouseEnter={e => { if (!active) { e.currentTarget.style.background = 'rgba(255,255,255,0.07)'; e.currentTarget.style.color = 'rgba(255,255,255,0.85)'; e.currentTarget.style.transform = 'translateX(2px)'; } }}
                  onMouseLeave={e => { if (!active) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'rgba(255,255,255,0.5)'; e.currentTarget.style.transform = 'translateX(0)'; } }}
                >
                  {/* active left bar — absolutely positioned, never affects flex centering */}
                  {active && <div style={{ position: 'absolute', left: 0, top: '20%', bottom: '20%', width: 3, borderRadius: '0 3px 3px 0', background: secColor[g.sec] || '#63b3ff', boxShadow: `0 0 8px ${secColor[g.sec] || '#63b3ff'}88`, pointerEvents: 'none' }} />}
                  {/* icon in a fixed 44px centered slot — always stays centered when collapsed */}
                  <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 44, minWidth: 44, flexShrink: 0, flexGrow: 0 }}>
                    <span className="material-symbols-outlined" style={{
                      fontSize: 20, lineHeight: 1,
                      color: active ? (secColor[g.sec] || '#63b3ff') : 'inherit',
                      filter: active ? `drop-shadow(0 0 4px ${secColor[g.sec] || '#63b3ff'}88)` : 'none',
                      transition: 'color 180ms,filter 180ms',
                    }}>{it.icon}</span>
                  </span>
                  {/* label */}
                  <span className="sb-label" style={{ whiteSpace: 'nowrap', opacity: 0, maxWidth: 0, overflow: 'hidden', transition: 'opacity 200ms,max-width 240ms', textAlign: 'left', flex: 1 }}>{it.label}</span>
                  {/* active pill badge */}
                  <span className="sb-active-pill" style={{
                    display: active ? 'block' : 'none',
                    marginRight: 10,
                    width: 6, height: 6, borderRadius: '50%', flexShrink: 0,
                    background: secColor[g.sec] || '#63b3ff',
                    boxShadow: `0 0 6px ${secColor[g.sec] || '#63b3ff'}`,
                    opacity: 0, transition: 'opacity 200ms',
                  }} />
                </button>
              );
            })}

            {/* section divider */}
            {gi < NAV.length - 1 && (
              <div style={{ height: 1, background: 'rgba(255,255,255,0.05)', margin: '6px 8px' }} />
            )}
          </div>
        ))}
      </nav>

    </aside>
  );
});


export default Sidebar;
